import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Trophy, Zap, Users, Timer, Sun, Moon } from "lucide-react";
import { currentSeason } from "@/lib/season";

export const RoleBadge = ({ role, className = "" }) => (
  <span
    data-testid={`role-badge-${role}`}
    className={`inline-flex items-center justify-center w-8 h-8 text-xs font-black rounded-md role-${role} ${className}`}
  >
    {role}
  </span>
);

export function TopNav() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-40 border-b border-white/5 bg-[#080E18]/80 backdrop-blur-xl theme-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2" data-testid="brand-logo">
          <div className="w-11 h-11 rounded-2xl bg-white overflow-hidden ring-2 ring-emerald-500/40 shadow-[0_0_20px_rgba(16,185,129,0.35)]">
            <img src="/logo.jpg" alt="AstaFantaLive" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="font-black text-lg leading-none tracking-tight theme-brand">AstaFantaLive</div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">Live</div>
          </div>
        </Link>
        <nav className="flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            className="w-9 h-9 rounded-lg border border-white/10 hover:border-emerald-500/50 flex items-center justify-center text-slate-300 hover:text-emerald-400 transition-all"
            title={theme === "light" ? "Passa al tema scuro" : "Passa al tema chiaro"}
            aria-label="Toggle theme"
          >
            {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
          </button>
          {user && (
            <>
              <Link to="/dashboard" className="text-sm text-slate-300 hover:text-white px-3 py-2" data-testid="nav-dashboard">Le mie sale</Link>
              <Link to="/players" className="text-sm text-slate-300 hover:text-white px-3 py-2" data-testid="nav-players">Calciatori</Link>
              {user.role !== "guest" && (
                <Link to="/admin/listone" className="text-sm text-emerald-400 hover:text-emerald-300 px-3 py-2 font-bold" data-testid="nav-listone">Listone</Link>
              )}
              <span className="hidden sm:inline text-xs text-slate-400 ml-2">{user.name}</span>
              <Button data-testid="logout-btn" variant="ghost" size="sm" onClick={async () => { await logout(); nav("/"); }}>
                Esci
              </Button>
            </>
          )}
          {!user && (
            <>
              <Link to="/login"><Button variant="ghost" size="sm" data-testid="nav-login">Accedi</Button></Link>
              <Link to="/register"><Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold" data-testid="nav-register">Registrati</Button></Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

export function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 pitch-bg" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 items-center">
          <div className="max-w-2xl">
            <div className="label-eyebrow mb-4" data-testid="hero-season">Il tempio dell'asta • Serie A {currentSeason()}</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6">
              L'asta del <span className="text-emerald-400">fantacalcio</span>,<br />
              come non l'hai mai vissuta.
            </h1>
            <p className="text-lg text-slate-300 mb-4 leading-relaxed">
              Crea la sala, sorteggia l'ordine di chiamata, rilancia in tempo reale con il timer che scorre.
              Ogni offerta, ogni aggiudicazione: tutto sotto controllo, professionale, senza carta e penna.
            </p>
            <p className="text-sm text-emerald-400/90 mb-8 font-bold">
              Solo il creatore della sala si registra. I partecipanti entrano direttamente dal link di invito.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register">
                <Button data-testid="hero-cta-start" size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-base h-12 px-8">
                  Inizia gratis
                </Button>
              </Link>
              <Link to="/login">
                <Button data-testid="hero-cta-login" variant="outline" size="lg" className="h-12 px-8 border-emerald-500/30 hover:border-emerald-500 hover:bg-emerald-500/10 text-white">
                  Accedi
                </Button>
              </Link>
            </div>
          </div>
          <div className="hidden lg:block relative">
            <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/20 via-transparent to-transparent rounded-full blur-3xl" />
            <div className="relative w-72 h-72 xl:w-80 xl:h-80 rounded-[2rem] bg-white overflow-hidden shadow-2xl ring-4 ring-emerald-500/20"
                 style={{ animation: "logoFloat 4s ease-in-out infinite" }}>
              <img src="/logo.jpg" alt="AstaFantaLive mascotte" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-16">
          {[
            { icon: Users, title: "Multi-utente", desc: "Tutti al tavolo, in tempo reale." },
            { icon: Timer, title: "Timer di rilancio", desc: "Countdown live, aggiudicazione automatica." },
            { icon: Zap, title: "Serie A precaricata", desc: "P/D/C/A pronti + import CSV." },
          ].map((f) => (
            <div key={f.title} className="glass rounded-2xl p-5 hover:border-emerald-500/40 transition-all">
              <f.icon className="w-6 h-6 text-emerald-400 mb-3" />
              <div className="font-bold text-white mb-1">{f.title}</div>
              <div className="text-sm text-slate-400">{f.desc}</div>
            </div>
          ))}
        </div>
      </div>
      <style>{`@keyframes logoFloat { 0%,100% { transform: translateY(0) rotate(-2deg) } 50% { transform: translateY(-14px) rotate(2deg) } }`}</style>
    </section>
  );
}
